/**
 * L'adreça d'una reserva.
 *
 * `/reserves/92725627` obre el calendari v2 amb la fitxa d'aquesta reserva; amb més d'un
 * número separats per `+`, un grup. L'identificador és sempre el número de Beds24 (8
 * xifres), mai el `cuid` intern: és el que la gent llegeix, el que surt als fils de l'OTA i
 * el que es pot enviar per WhatsApp (decisió de Pere, spec §2).
 *
 * En llegir s'accepten `+` i `,` com a separadors — un client de correu o de missatgeria pot
 * reescriure l'enllaç —, però en escriure sempre es fa servir `+` fins que M1 decideixi.
 */

export const CALENDAR_PATH = '/calendar-v2';
export const RESERVATION_PATH_PREFIX = '/reserves';

/** Un número de reserva de Beds24: exactament 8 xifres. */
const BEDS24_ID = /^\d{8}$/;

/** Màxim de reserves d'un grup a l'URL. Prou per a un grup real i tallem l'abús. */
const MAX_GROUP_SIZE = 6;

/**
 * Parseja el paràmetre `:ids` de la ruta.
 *
 * Torna els números vàlids, sense repetits i en l'ordre de l'URL. Si hi ha **cap** número
 * invàlid, torna una llista buida: val més enviar a `/calendar-v2` amb un avís que obrir
 * una fitxa que no és la que s'esperava.
 */
export function parseReservationIds(param: string | undefined | null): string[] {
  if (!param) return [];

  // `decodeURIComponent` llança `URIError` amb un `%` solt (`/reserves/92725627%`). Això
  // passa dins d'un `useMemo` durant el render: sense el `try`, la pàgina peta sencera en
  // comptes d'avisar i tornar al calendari.
  let decoded: string;
  try {
    decoded = decodeURIComponent(param);
  } catch {
    return [];
  }

  const parts = decoded
    .split(/[+,]/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  if (parts.length === 0 || parts.length > MAX_GROUP_SIZE) return [];
  if (!parts.every((part) => BEDS24_ID.test(part))) return [];

  return [...new Set(parts)];
}

/** `['92725627', '92725628']` → `/reserves/92725627+92725628` */
export function buildReservationPath(ids: Array<string | null | undefined>): string {
  const valid = [...new Set(ids.filter((id): id is string => !!id && BEDS24_ID.test(id)))];
  if (valid.length === 0) return CALENDAR_PATH;
  return `${RESERVATION_PATH_PREFIX}/${valid.slice(0, MAX_GROUP_SIZE).join('+')}`;
}

/**
 * Posa `id` al davant del grup, mantenint la resta. És el que fa clicar una reserva de la
 * barra de grup: canvia quina fitxa es veu sense perdre l'enllaç que va arribar.
 */
export function buildGroupPathFocusedOn(id: string, groupIds: string[]): string {
  return buildReservationPath([id, ...groupIds]);
}

/** El dilluns de la setmana d'una data, a les 00:00 locals. */
export function startOfWeekMonday(date: Date): Date {
  const monday = new Date(date);
  monday.setHours(0, 0, 0, 0);
  const daysSinceMonday = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - daysSinceMonday);
  return monday;
}
